import React, { useContext, useState } from 'react'

import { Heading1, Label, SubParagraph, StrongText } from '@entur/typography'
import { space } from '@entur/tokens'
import { GridContainer, GridItem } from '@entur/grid'
import { BannerAlertBox } from '@entur/alert'
import { Button } from '@entur/button'
import { DeleteIcon, SaveIcon } from '@entur/icons'

import { useEmployee } from '../../contexts/employee-context'
import { BackButton } from '../layout/back-button'
import EmployeeSearchNavigation from './employee-search-navigation'

import { toPrintFormat } from '../../utils/date-utils'
import { EmployeeOverflowMenu } from './employee-overflow-menu'
import CopyButton from '../copy-button/copy-button'
import { useAcceptDraft, useRejectDraft } from '../../hooks/use-draft-hooks'
import { FetchState } from '../../api-clients/api-types'
import { TechnicalError } from '../error/error'
import { DraftValue } from '../draft-value/draft-value'
import DeleteModal from '../delete-modal/delete-modal'
import draftUpdateDate from './draft-update-date'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'

type EmployeeHeaderProps = {
  disabled?: boolean
}
export const EmployeeHeader: React.FC<EmployeeHeaderProps> = ({ disabled }) => {
  const { employee } = useEmployee()
  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)
  const [errorMsg, setErrorMsg] = useState<string | undefined>()
  const [showModal, setShowModal] = useState(false)
  const [acceptDraftResponse, acceptDraft] = useAcceptDraft()
  const [rejectDraftResponse, rejectDraft] = useRejectDraft()

  const currentFullName = `${employee.firstName} ${employee.surname}`
  const currentAddress = `${employee.addressLine}, ${employee.postalPlace.code} ${employee.postalPlace.town}`

  const hasEmployeeAndDraft = employee && employee.draftEmployee
  return (
    <>
      <DeleteModal
        title="Slette utkast?"
        description={`Er du sikker på at du vil slette utkastet til ${employee.firstName} ${employee.surname}?`}
        negativeText="Slett utkast"
        positiveText="Behold utkast"
        action={rejectDraft}
        open={showModal}
        setOpen={setShowModal}
      />
      <GridContainer
        style={{
          position: 'relative',
        }}
      >
        <GridItem small={6} medium={6}>
          <BackButton disabled={disabled} />
        </GridItem>
        <GridItem
          small={6}
          medium={6}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <EmployeeOverflowMenu disabled={disabled} setErrorMessage={setErrorMsg} />
          <EmployeeSearchNavigation disabled={disabled} />
        </GridItem>
        {errorMsg && (
          <GridItem small={12}>
            <BannerAlertBox
              variant="error"
              title={errorMsg}
              closable={true}
              onClose={() => setErrorMsg(undefined)}
              style={{ marginBottom: space.medium }}
            >
              Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
            </BannerAlertBox>
          </GridItem>
        )}
        {readOnly && (
          <GridItem small={12}>
            <BannerAlertBox
              style={{ marginBottom: space.medium }}
              title="Profilen er markert slettet og kan derfor ikke endres"
              variant="warning"
              data-cy="deleted-profile-warning"
            >
              {' '}
            </BannerAlertBox>
          </GridItem>
        )}
        <GridItem small={12}>
          <Heading1
            style={{ float: 'left', margin: 0 }}
            className={`profile-page__header ${disabled ? 'disabled' : ''}`}
            data-cy="profile-page-header"
          >
            <DraftValue
              currentValue={currentFullName}
              draftValue={
                employee.draftEmployee && `${employee.draftEmployee.surname}, ${employee.draftEmployee.firstName}`
              }
            />
            <CopyButton value={currentFullName} description="Navn" size="14" />
            <Label style={{ marginLeft: space.small }}>
              {employee.id} ({employee.company?.name})
            </Label>
          </Heading1>
          {hasEmployeeAndDraft && (
            <div style={{ float: 'right' }}>
              <Button
                id="deleteDraftBtn"
                variant="secondary"
                data-cy="delete-draft"
                style={{ marginRight: space.extraSmall }}
                onClick={() => setShowModal(true)}
              >
                <DeleteIcon />
                Slett utkast
              </Button>
              <Button
                id="acceptDraftBtn"
                data-cy="accept-draft-button"
                variant="success"
                loading={acceptDraftResponse.status === FetchState.LOADING}
                disabled={acceptDraftResponse.status === FetchState.LOADING}
                onClick={acceptDraft}
              >
                <SaveIcon />
                Godkjenn utkast
              </Button>
            </div>
          )}
        </GridItem>
        <GridItem small={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <SubParagraph
            data-cy="profile-page-header-paragraph"
            className="profile-page__header-paragraph"
            style={{ marginTop: 0 }}
          >
            <StrongText>Adresse: </StrongText>{' '}
            <DraftValue
              currentValue={currentAddress}
              draftValue={
                employee.draftEmployee &&
                `${employee.draftEmployee.addressLine}, ${employee.draftEmployee.postalPlace.code} ${employee.draftEmployee.postalPlace.town}`
              }
            />
            &emsp;
            <StrongText>Fødselsnummer:</StrongText> {toPrintFormat(employee.dateOfBirth)}{' '}
            {hasSensitiveAccess && employee.ssn >= 0 ? employee.ssn : ''}
            <CopyButton value={toPrintFormat(employee.dateOfBirth)} description="Fødselsdato" />
          </SubParagraph>
          <SubParagraph style={{ marginTop: 0 }}>
            {hasEmployeeAndDraft && draftUpdateDate(employee.draftEmployee)}
          </SubParagraph>
        </GridItem>
        {acceptDraftResponse.status === FetchState.ERROR && (
          <GridItem small={12}>
            <TechnicalError title="Utkastet ble ikke godkjent" content="En teknisk feil oppstod." />
          </GridItem>
        )}
        {rejectDraftResponse.status === FetchState.ERROR && (
          <GridItem small={12}>
            <TechnicalError title="Utkastet kunne ikke slettes" content="En teknisk feil oppstod." />
          </GridItem>
        )}
      </GridContainer>
    </>
  )
}
